/**
 * Live-history store — a bounded ring buffer of recent telemetry per edge/node,
 * so the details sections can draw sparklines (rate / error / latency trend)
 * instead of a bare instantaneous number. Sibling to the stats store: the host
 * pushes one point per key per SSE frame; the store keeps the last N.
 *
 * Keyed the same way as the stats/overlay stores (trafficEdgeKey / normServiceName)
 * so a board module id resolves the same series. Absent provider → NOOP → the
 * sections render no sparkline.
 */
import { createContext, useContext, useSyncExternalStore } from "react";
import { normServiceName, trafficEdgeKey } from "./liveTrafficStore.js";

export interface HistoryPoint {
  readonly at: number;
  readonly perSec: number;
  readonly errorRate?: number;
  readonly p50?: number;
  readonly p95?: number;
  readonly p99?: number;
}

/** Max points retained per key (~2 min at a 2s poll). */
export const HISTORY_MAX = 60;

export type HistoryMap = ReadonlyMap<string, readonly HistoryPoint[]>;

export interface LiveHistoryStore {
  subscribe(onChange: () => void): () => void;
  getHistory(): HistoryMap;
  /** Append one point per key for this frame; caps each series at HISTORY_MAX. */
  push(points: Iterable<readonly [string, HistoryPoint]>): void;
  clear(): void;
}

const EMPTY: HistoryMap = new Map();

export function createLiveHistoryStore(max = HISTORY_MAX): LiveHistoryStore {
  let history: Map<string, readonly HistoryPoint[]> = new Map();
  const listeners = new Set<() => void>();
  return {
    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    getHistory() {
      return history;
    },
    push(points) {
      let changed = false;
      const next = new Map(history);
      for (const [key, point] of points) {
        const prev = next.get(key) ?? [];
        const series = prev.length >= max ? [...prev.slice(prev.length - max + 1), point] : [...prev, point];
        next.set(key, series);
        changed = true;
      }
      if (!changed) return;
      history = next;
      for (const l of listeners) l();
    },
    clear() {
      if (history.size === 0) return;
      history = new Map();
      for (const l of listeners) l();
    },
  };
}

const Ctx = createContext<LiveHistoryStore | null>(null);
export const LiveHistoryProvider = Ctx.Provider;

const NOOP: LiveHistoryStore = {
  subscribe: () => () => {},
  getHistory: () => EMPTY,
  push: () => {},
  clear: () => {},
};

export function useLiveHistoryStore(): LiveHistoryStore | null {
  return useContext(Ctx);
}

function useHistory(): HistoryMap {
  const store = useContext(Ctx) ?? NOOP;
  return useSyncExternalStore(store.subscribe, store.getHistory, store.getHistory);
}

/** Recent series for a directed edge (empty when none). */
export function useEdgeHistory(from: string, to: string): readonly HistoryPoint[] {
  return useHistory().get(trafficEdgeKey(from, to)) ?? [];
}

/** Recent series for a node (empty when none). */
export function useNodeHistory(moduleOrService: string): readonly HistoryPoint[] {
  return useHistory().get(normServiceName(moduleOrService)) ?? [];
}
